import { createSlice } from "@reduxjs/toolkit";

interface ZendeskLocalStorageData {
  status?: "hidden" | "shown";
  timestamp?: number;
  tooltipDismissed?: boolean;
}

export interface ZendeskNotificationSliceState {
  chatHidden: boolean;
  tooltipDismissed: boolean;
  zendeskLoaded: boolean;
}

const ZENDESK_LOCAL_STORAGE_KEY = "n32Zendesk";

const isDataStale = (timestamp?: number): boolean => {
  if (!timestamp) return true;
  const twoHours = 2 * 60 * 60 * 1000;
  return timestamp < Date.now() - twoHours;
};

const updateLocalStorage = (updates: Partial<ZendeskLocalStorageData>) => {
  if (typeof window === "undefined") return;
  try {
    const data = JSON.parse(localStorage.getItem(ZENDESK_LOCAL_STORAGE_KEY) || "{}");
    localStorage.setItem(ZENDESK_LOCAL_STORAGE_KEY, JSON.stringify({ ...data, ...updates }));
  } catch (e) {
    console.warn("Could not update Zendesk localStorage:", e);
  }
};

// Initialize from localStorage if available
const getInitialState = (): ZendeskNotificationSliceState => {
  if (typeof window === "undefined") {
    return {
      chatHidden: true,
      tooltipDismissed: false,
      zendeskLoaded: false,
    };
  }
  const zendeskLoaded = typeof window.zE === "function";
  try {
    const zendeskData: ZendeskLocalStorageData = JSON.parse(localStorage.getItem(ZENDESK_LOCAL_STORAGE_KEY) || "{}");
    return {
      chatHidden: zendeskData.status === "hidden" || isDataStale(zendeskData.timestamp),
      tooltipDismissed: zendeskData.tooltipDismissed || false,
      zendeskLoaded,
    };
  } catch (e) {
    return {
      chatHidden: true,
      tooltipDismissed: false,
      zendeskLoaded,
    };
  }
};

const initialState: ZendeskNotificationSliceState = getInitialState();

export const zendeskNotificationSlice = createSlice({
  name: "zendeskNotificationSlice",
  initialState,
  reducers: {
    hideChat: (state) => {
      state.chatHidden = true;
      updateLocalStorage({ status: "hidden", timestamp: Date.now() });
    },
    showChat: (state) => {
      state.chatHidden = false;
      updateLocalStorage({ status: "shown", timestamp: Date.now() });
    },
    dismissTooltip: (state) => {
      state.tooltipDismissed = true;
      updateLocalStorage({ tooltipDismissed: true });
    },
    setZendeskLoaded: (state) => {
      state.zendeskLoaded = true;
    },
  },
});

export const { hideChat, showChat, dismissTooltip, setZendeskLoaded } = zendeskNotificationSlice.actions;
export default zendeskNotificationSlice;
